import { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal } from "@/components/Reveal";
import { WORK_SAMPLES } from "@/data/site";
import { ChevronLeft, ChevronRight, X, Maximize2 } from "lucide-react";

export function WorkSamples() {
  const trackRef = useRef(null);
  const [light, setLight] = useState(null);

  const scroll = (dir) => {
    trackRef.current?.scrollBy({ left: dir * 420, behavior: "smooth" });
  };

  return (
    <section id="samples" className="relative overflow-hidden py-24 lg:py-32" data-testid="work-samples">
      <div className="ambient-magenta pointer-events-none absolute left-0 top-10 h-[400px] w-[400px] opacity-50" />
      <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
        <Reveal>
          <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
            <div>
              <p className="mb-4 font-mono text-xs uppercase tracking-[0.3em] text-orange">Work Samples</p>
              <h2 className="max-w-2xl font-display text-4xl font-extrabold leading-none tracking-tight sm:text-5xl lg:text-6xl">
                A+ content &amp; storefronts, live.
              </h2>
              <p className="mt-5 max-w-lg text-sm font-light leading-relaxed text-muted">
                Real creative from real accounts. Drag through the deck — tap any sample to read it full-screen.
              </p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => scroll(-1)} data-testid="samples-prev" className="rounded-full border border-white/15 p-3 text-white transition-colors hover:border-orange hover:text-orange">
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button onClick={() => scroll(1)} data-testid="samples-next" className="rounded-full border border-white/15 p-3 text-white transition-colors hover:border-orange hover:text-orange">
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          </div>
        </Reveal>
      </div>

      <div
        ref={trackRef}
        className="mt-12 flex gap-5 overflow-x-auto px-5 pb-6 lg:px-8"
        style={{ scrollbarWidth: "none", scrollSnapType: "x mandatory" }}
        data-testid="samples-track"
      >
        {WORK_SAMPLES.map((s, i) => (
          <button
            key={i}
            onClick={() => setLight(s)}
            data-testid={`sample-${i}`}
            className="group relative shrink-0 overflow-hidden rounded-xl border border-white/8 bg-surface"
            style={{ width: "min(78vw, 380px)", scrollSnapAlign: "start" }}
          >
            <img src={s.img} alt={`${s.brand} ${s.type}`} className="h-[440px] w-full object-cover object-top transition-transform duration-700 group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
            <div className="absolute inset-x-0 bottom-0 flex items-center justify-between p-5">
              <div className="text-left">
                <div className="font-display text-lg font-bold text-white">{s.brand}</div>
                <div className="font-mono text-[11px] uppercase tracking-widest text-orange">{s.type}</div>
              </div>
              <span className="rounded-full bg-white/10 p-2 backdrop-blur transition-colors group-hover:bg-orange">
                <Maximize2 className="h-4 w-4 text-white" />
              </span>
            </div>
          </button>
        ))}
      </div>

      <AnimatePresence>
        {light && (
          <motion.div
            className="fixed inset-0 z-[200] flex items-center justify-center p-4"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            data-testid="sample-lightbox"
          >
            <div className="absolute inset-0 bg-black/90 backdrop-blur-sm" onClick={() => setLight(null)} />
            <button onClick={() => setLight(null)} className="absolute right-5 top-5 z-10 rounded-full bg-white/10 p-3 text-white hover:bg-white/20" data-testid="lightbox-close">
              <X className="h-5 w-5" />
            </button>
            <motion.div
              initial={{ scale: 0.96 }} animate={{ scale: 1 }} exit={{ scale: 0.96 }}
              className="relative z-[1] max-h-[90vh] overflow-y-auto rounded-xl"
            >
              <div className="mb-3 text-center">
                <span className="font-display text-lg font-bold text-white">{light.brand}</span>
                <span className="ml-3 font-mono text-xs uppercase tracking-widest text-orange">{light.type}</span>
              </div>
              <img src={light.img} alt={`${light.brand} ${light.type}`} className="mx-auto max-h-[80vh] w-auto rounded-lg" />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
